import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { signInWithPopup, createUserWithEmailAndPassword, updateProfile, sendEmailVerification, getAuth } from 'firebase/auth';
import { auth, googleProvider, db, getUserData } from '../firebase';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { toast } from 'react-hot-toast';
import { Mail, Lock, Eye, EyeOff, User } from 'lucide-react';
import BrandSignInTokenService from '../services/brandSignInTokenService';
import { checkInviteEmailAccountType } from '../services/brandInviteEmailService';
import {
  getAccountTypeMessage,
  getDenialMessageFromUserData,
  shouldRedirectToCreatorDashboard,
  isBrandDashboardAllowedUserType,
  getCreatorDashboardSignInUrl,
} from '../utils/accountTypeMessages';
import { getApiUrl } from '../config/api';
import './BrandOnboarding.css';

const BrandOnboarding = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Token validation state
  const [isValidatingToken, setIsValidatingToken] = useState(true);
  const [loadingMessage, setLoadingMessage] = useState('Validating sign-up link...');
  const [tokenInfo, setTokenInfo] = useState(null);
  const [tokenError, setTokenError] = useState(null);
  
  // Auth state
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [useEmailPassword, setUseEmailPassword] = useState(false);
  const [emailPasswordData, setEmailPasswordData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [existingAccountHint, setExistingAccountHint] = useState(null);
  
  // Validate token on mount
  useEffect(() => {
    const token = searchParams.get('token');
    
    if (!token) {
      setTokenError('No sign-up token provided. Please use the link from your invitation email.');
      setIsValidatingToken(false);
      return;
    }
    
    const validateToken = async () => {
      try {
        setLoadingMessage('Validating sign-up link...');
        const result = await BrandSignInTokenService.validateToken(token);
        
        if (!result.valid) {
          setTokenError(result.error || 'Invalid or expired sign-up link');
          setIsValidatingToken(false);
          return;
        }
        
        // Store token info
        setTokenInfo({
          token,
          ...result.data
        });
        
        // Pre-fill email if available
        if (result.data.brandEmail) {
          setEmailPasswordData(prev => ({
            ...prev,
            email: result.data.brandEmail
          }));
          const hint = await checkInviteEmailAccountType(result.data.brandEmail, token);
          if (hint?.accountType && hint.accountType !== 'none') {
            setExistingAccountHint(hint);
          }
        }
        
        setIsValidatingToken(false);
      } catch (error) {
        console.error('Error validating token:', error);
        setTokenError('Failed to validate sign-up link. Please try again.');
        setIsValidatingToken(false);
      }
    };
    
    validateToken();
  }, [searchParams]);
  
  // Password validation
  const validatePassword = (password) => {
    if (password.length < 8) {
      return { valid: false, error: 'Password must be at least 8 characters' };
    }
    if (!/[A-Z]/.test(password)) {
      return { valid: false, error: 'Password must contain at least one capital letter' };
    }
    if (!/[a-z]/.test(password)) {
      return { valid: false, error: 'Password must contain at least one lowercase letter' };
    }
    if (!/[0-9]/.test(password)) {
      return { valid: false, error: 'Password must contain at least one number' };
    }
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
      return { valid: false, error: 'Password must contain at least one special character (!@#$%^&* etc.)' };
    }
    return { valid: true };
  };
  
  // Email validation
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };
  
  // Helper function to mark token as used via backend
  const markTokenAsUsed = async (token, tokenType) => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        console.warn('No authenticated user to mark token as used');
        return;
      }
      
      console.log('Marking token as used via backend...');
      const idToken = await currentUser.getIdToken();
      
      // Use relative path - Firebase Hosting will rewrite /api/* to Cloud Run
      const response = await fetch('/api/brand/mark-token-used', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({
          token: token,
          tokenType: tokenType || 'signup'
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        console.log('Token marked as used successfully:', result);
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        console.warn('Could not mark token as used (non-critical):', errorData.error || response.statusText);
      }
    } catch (tokenError) {
      console.warn('Could not mark token as used (non-critical):', tokenError);
    }
  };
  
  const rejectWrongAccountType = async (profileData) => {
    if (!profileData?.userType) return false;
    if (shouldRedirectToCreatorDashboard(profileData.userType)) {
      toast.error(getDenialMessageFromUserData(profileData), { duration: 8000 });
      await auth.signOut();
      window.location.href = getCreatorDashboardSignInUrl();
      return true;
    }
    if (!isBrandDashboardAllowedUserType(profileData.userType)) {
      toast.error(getDenialMessageFromUserData(profileData), { duration: 8000 });
      await auth.signOut();
      return true;
    }
    return false;
  };

  const getEmailConflictMessage = async (email) => {
    const token = searchParams.get('token');
    if (token && email) {
      const hint = await checkInviteEmailAccountType(email, token);
      if (hint?.accountType) {
        return getAccountTypeMessage(hint.accountType, {
          authProviders: hint.authProviders || [],
          context: 'onboarding',
        });
      }
    }
    return getAccountTypeMessage('none', { context: 'onboarding' });
  };

  // Google Sign-In handler
  const handleGoogleSignIn = async () => {
    if (!tokenInfo) {
      toast.error('Invalid sign-up link. Please use the link from your invitation email.');
      return;
    }
    
    setIsSigningIn(true);
    
    try {
      console.log('Starting Google Sign-In for brand...');
      
      // Sign in with Google
      const userCredential = await signInWithPopup(auth, googleProvider);
      const firebaseUser = userCredential.user;
      
      console.log('Google Sign-In successful:', firebaseUser.uid);
      
      // Validate email matches token email for signup tokens
      if (tokenInfo.tokenType === 'signup' && tokenInfo.brandEmail) {
        if (firebaseUser.email?.toLowerCase() !== tokenInfo.brandEmail.toLowerCase()) {
          toast.error(`This sign-up link is for ${tokenInfo.brandEmail}. Please use the correct Google account.`);
          await auth.signOut();
          setIsSigningIn(false);
          return;
        }
      }
      
      // Check if user already exists in Firestore
      const userDocRef = doc(db, 'users', firebaseUser.uid);
      const userDoc = await getDoc(userDocRef);
      
      const brandName = firebaseUser.displayName || tokenInfo.brandName || 'Brand';
      
      if (userDoc.exists()) {
        // User already exists - update with token data if needed
        console.log('User already exists in Firestore');
        const existingData = userDoc.data();

        if (await rejectWrongAccountType(existingData)) {
          setIsSigningIn(false);
          return;
        }
        
        const updates = {};
        if (tokenInfo.tokenType === 'signup') {
          if (!existingData.brandName || existingData.brandName !== tokenInfo.brandName) {
            updates.brandName = tokenInfo.brandName || brandName;
          }
          if (!existingData.brandEmail || existingData.brandEmail !== tokenInfo.brandEmail) {
            updates.brandEmail = tokenInfo.brandEmail || firebaseUser.email;
          }
          if (!existingData.userType) {
            updates.userType = 'client';
          }
        }
        
        if (Object.keys(updates).length > 0) {
          await setDoc(userDocRef, updates, { merge: true });
          console.log('Updated user document with token data');
        }
        
        toast.success('Welcome back! Redirecting to onboarding...');
      } else {
        // Create new user document in Firestore
        console.log('Creating new brand user document...');
        const userData = {
          email: firebaseUser.email || tokenInfo.brandEmail || '',
          brandName: tokenInfo.brandName || brandName,
          brandEmail: tokenInfo.brandEmail || firebaseUser.email || '',
          userType: 'client',
          credits: 0,
          walletBalance: 0,
          createdAt: new Date().toISOString(),
          onboardingStatus: {
            completed: false
          }
        };
        
        await setDoc(userDocRef, userData);
        console.log('Brand user document created successfully');
      }
      
      // Mark token as used via backend
      const token = searchParams.get('token');
      if (token) {
        await markTokenAsUsed(token, tokenInfo.tokenType || 'signup');
      }
      
      toast.success('Account created successfully! Redirecting to onboarding...');
      
      // Redirect to dashboard (RequireOnboarding will show ClientOnboardingForm if needed)
      navigate('/dashboard');
      
    } catch (error) {
      console.error('Google Sign-In error:', error);
      
      if (error.code === 'auth/popup-closed-by-user') {
        toast.error('Sign-in cancelled. Please try again.');
      } else if (error.code === 'auth/popup-blocked') {
        toast.error('Popup blocked. Please allow popups and try again.');
      } else {
        toast.error(`Sign-in failed: ${error.message || 'Please try again.'}`);
      }
    } finally {
      setIsSigningIn(false);
    }
  };
  
  // Email/Password account creation handler
  const handleEmailPasswordSignUp = async () => {
    if (!tokenInfo) {
      toast.error('Invalid sign-up link. Please use the link from your invitation email.');
      return;
    }
    
    // Validation
    if (!emailPasswordData.email || !emailPasswordData.password || !emailPasswordData.confirmPassword) {
      toast.error('Please fill in all fields');
      return;
    }
    
    if (!validateEmail(emailPasswordData.email)) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    // Validate email matches token email for signup tokens
    if (tokenInfo.tokenType === 'signup' && tokenInfo.brandEmail) {
      if (emailPasswordData.email.toLowerCase() !== tokenInfo.brandEmail.toLowerCase()) {
        toast.error(`This sign-up link is for ${tokenInfo.brandEmail}. Please use the correct email address.`);
        return;
      }
    }
    
    if (emailPasswordData.password !== emailPasswordData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    // Password validation
    const passwordValidation = validatePassword(emailPasswordData.password);
    if (!passwordValidation.valid) {
      toast.error(passwordValidation.error);
      return;
    }
    
    setIsSigningIn(true);
    
    try {
      console.log('Creating brand account with email/password...');
      
      let userCredential;
      
      // Create account only — do not auto sign-in on email-already-in-use with a new
      // password (existing accounts are often Google/creator and will fail with invalid-credential)
      userCredential = await createUserWithEmailAndPassword(
        auth,
        emailPasswordData.email,
        emailPasswordData.password
      );
      
      // Update user profile with name from token
      const displayName = tokenInfo?.brandName || 'Brand';
      await updateProfile(userCredential.user, {
        displayName: displayName
      });
      
      // Send email verification
      await sendEmailVerification(userCredential.user);
      console.log('Verification email sent');
      
      const firebaseUser = userCredential.user;
      console.log('Account created/signed in successfully:', firebaseUser.uid);
      
      // Check if user document exists
      const userDocRef = doc(db, 'users', firebaseUser.uid);
      const userDoc = await getDoc(userDocRef);
      
      const brandName = tokenInfo.brandName || 'Brand';
      
      if (!userDoc.exists()) {
        // Create new user document
        const userData = {
          email: emailPasswordData.email,
          brandName: tokenInfo.brandName || brandName,
          brandEmail: tokenInfo.brandEmail || emailPasswordData.email,
          userType: 'client',
          credits: 0,
          walletBalance: 0,
          createdAt: new Date().toISOString(),
          onboardingStatus: {
            completed: false
          }
        };
        
        await setDoc(userDocRef, userData);
        console.log('Brand user document created successfully');
      } else {
        // Update existing document with token data
        const existingData = userDoc.data();
        const updates = {};
        
        if (tokenInfo.tokenType === 'signup') {
          if (!existingData.brandName || existingData.brandName !== tokenInfo.brandName) {
            updates.brandName = tokenInfo.brandName || brandName;
          }
          if (!existingData.brandEmail || existingData.brandEmail !== tokenInfo.brandEmail) {
            updates.brandEmail = tokenInfo.brandEmail || emailPasswordData.email;
          }
          if (!existingData.userType) {
            updates.userType = 'client';
          }
        }
        
        if (Object.keys(updates).length > 0) {
          await setDoc(userDocRef, updates, { merge: true });
          console.log('Updated user document with token data');
        }
      }
      
      // Mark token as used via backend
      const token = searchParams.get('token');
      if (token) {
        await markTokenAsUsed(token, tokenInfo.tokenType || 'signup');
      }
      
      toast.success('Account created successfully! Please check your email to verify your account.');
      
      // Redirect to dashboard (RequireOnboarding will show ClientOnboardingForm if needed)
      navigate('/dashboard');
      
    } catch (error) {
      console.error('Email/password signup error:', error);
      if (error.code === 'auth/email-already-in-use') {
        const message = await getEmailConflictMessage(emailPasswordData.email);
        toast.error(message, { duration: 8000 });
      } else if (error.code === 'auth/invalid-credential' || error.code === 'auth/wrong-password') {
        const message = await getEmailConflictMessage(emailPasswordData.email);
        toast.error(message, { duration: 8000 });
      } else if (error.code === 'auth/weak-password') {
        toast.error('Password is too weak. Please use a stronger password.');
      } else if (error.code === 'auth/operation-not-allowed') {
        toast.error('Email/password sign-up is not enabled. Please use Continue with Google.');
      } else {
        toast.error(`Failed to create account: ${error.message || 'Please try again.'}`);
      }
    } finally {
      setIsSigningIn(false);
    }
  };
  
  // Loading state
  if (isValidatingToken) {
    return (
      <div className="brand-onboarding-container">
        <div className="brand-onboarding-loading">
          <img 
            src="/ugc3-logo.png" 
            alt="UGC3 Logo" 
            style={{ 
              width: '60px',
              height: '60px',
              objectFit: 'contain',
              marginBottom: '1.5rem'
            }} 
          />
          {/* Spinning wheel - matching creator onboarding style */}
          <div style={{
            width: '60px',
            height: '60px',
            border: '4px solid #f3f4f6',
            borderTop: '4px solid transparent',
            borderRadius: '50%',
            background: 'conic-gradient(from 0deg, #f8049c, #f97316, #f8049c)',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 1.5rem auto',
            position: 'relative'
          }}>
            <div style={{
              position: 'absolute',
              top: '4px',
              left: '4px',
              right: '4px',
              bottom: '4px',
              background: 'white',
              borderRadius: '50%'
            }} />
          </div>
          {/* Loading message */}
          <p style={{
            fontSize: '1rem',
            color: '#6b7280',
            fontWeight: 400,
            margin: 0,
            lineHeight: 1.5,
            minHeight: '1.5em',
            transition: 'opacity 0.3s ease'
          }}>
            {loadingMessage}
          </p>
        </div>
      </div>
    );
  }
  
  // Error state
  if (tokenError) {
    return (
      <div className="brand-onboarding-container">
        <div className="brand-onboarding-error">
          <h2>Invalid Sign-Up Link</h2>
          <p>{tokenError}</p>
          <button onClick={() => navigate('/signin')} className="btn-primary">
            Go to Sign In
          </button>
        </div>
      </div>
    );
  }
  
  // Auth selection or form
  return (
    <div style={{ 
      minHeight: '100vh', 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center', 
      justifyContent: 'center',
      padding: '2rem',
      background: 'linear-gradient(135deg, #fff8f9 0%, #fffdf4 100%)'
    }}>
      <div style={{ 
        maxWidth: '500px', 
        width: '100%', 
        background: 'white', 
        borderRadius: '16px', 
        padding: '3rem',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
      }}>
        {/* UGC3 Logo at the top */}
        <div style={{ textAlign: 'center', marginBottom: '2.5rem' }}>
          <img 
            src="/ugc3-logo.png" 
            alt="UGC3" 
            style={{ 
              width: '120px', 
              height: 'auto',
              maxWidth: '100%'
            }} 
          />
        </div>
        
        <div style={{ marginTop: '0' }}>
          {!useEmailPassword ? (
            <div style={{ textAlign: 'center' }}>
              <h2 style={{ marginBottom: '0.5rem', fontSize: '1.5rem', fontWeight: 600, color: '#0f172a' }}>
                Create your UGC3 brand account
              </h2>
              <p style={{ marginBottom: '1.5rem', color: '#64748b', fontSize: '1rem' }}>
                Use Google for a faster sign up, or create an account with your email.
              </p>
              {tokenInfo?.brandName && (
                <p style={{ marginBottom: '1.5rem', color: '#f8049c', fontSize: '0.875rem', fontWeight: 500 }}>
                  Signing up as: <strong>{tokenInfo.brandName}</strong>
                </p>
              )}

              {existingAccountHint?.accountType && existingAccountHint.accountType !== 'none' && (
                <div
                  role="alert"
                  style={{
                    marginBottom: '1.25rem',
                    padding: '0.875rem 1rem',
                    borderRadius: '8px',
                    background: '#fff7ed',
                    border: '1px solid #fed7aa',
                    color: '#9a3412',
                    fontSize: '0.875rem',
                    lineHeight: 1.5,
                    textAlign: 'left',
                  }}
                >
                  {getAccountTypeMessage(existingAccountHint.accountType, {
                    authProviders: existingAccountHint.authProviders || [],
                    context: 'onboarding',
                  })}
                </div>
              )}
              
              <button
                type="button"
                onClick={handleGoogleSignIn}
                disabled={isSigningIn}
                style={{
                  width: '100%',
                  maxWidth: '400px',
                  padding: '0.875rem 1.5rem',
                  fontSize: '1rem',
                  fontWeight: 600,
                  borderRadius: '12px',
                  border: 'none',
                  background: 'white',
                  color: '#1f2937',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                  cursor: isSigningIn ? 'not-allowed' : 'pointer',
                  opacity: isSigningIn ? 0.7 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.75rem',
                  margin: '0 auto 1rem',
                  transition: 'all 0.2s ease'
                }}
              >
                {isSigningIn ? (
                  <>
                    <div className="spinner-small"></div>
                    <span>Signing in...</span>
                  </>
                ) : (
                  <>
                    <svg width="20" height="20" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    <span>Sign up with Google</span>
                  </>
                )}
              </button>
              
              <div style={{ margin: '1.5rem 0', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                <div style={{ flex: 1, height: '1px', background: '#e5e7eb' }}></div>
                <span style={{ color: '#9ca3af', fontSize: '0.875rem' }}>or</span>
                <div style={{ flex: 1, height: '1px', background: '#e5e7eb' }}></div>
              </div>
              
              <button
                type="button"
                onClick={() => setUseEmailPassword(true)}
                disabled={isSigningIn}
                style={{
                  width: '100%',
                  maxWidth: '400px',
                  padding: '0.875rem 1.5rem',
                  fontSize: '1rem',
                  fontWeight: 600,
                  borderRadius: '12px',
                  border: '1px solid #d1d5db',
                  background: 'white',
                  color: '#374151',
                  cursor: isSigningIn ? 'not-allowed' : 'pointer',
                  margin: '0 auto',
                  transition: 'all 0.2s ease'
                }}
              >
                Create account with email
              </button>
            </div>
          ) : (
            <div style={{ textAlign: 'left', maxWidth: '400px', margin: '0 auto' }}>
              <button
                type="button"
                onClick={() => setUseEmailPassword(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#6b7280',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  marginBottom: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                ← Back
              </button>
              
              <form onSubmit={(e) => { e.preventDefault(); handleEmailPasswordSignUp(); }}>
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, fontSize: '0.9rem' }}>Email Address</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem', border: '1px solid #d1d5db', borderRadius: '8px', background: tokenInfo?.brandEmail ? '#f9fafb' : 'white' }}>
                    <Mail size={20} style={{ color: '#9ca3af', flexShrink: 0 }} />
                    <input
                      type="email"
                      value={emailPasswordData.email}
                      onChange={(e) => setEmailPasswordData(prev => ({ ...prev, email: e.target.value }))}
                      placeholder="your@email.com"
                      required
                      disabled={!!tokenInfo?.brandEmail}
                      style={{ flex: 1, border: 'none', background: 'transparent', outline: 'none', color: tokenInfo?.brandEmail ? '#6b7280' : '#1f2937', minWidth: 0, fontSize: '1rem' }}
                    />
                  </div>
                  {tokenInfo?.brandEmail && (
                    <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.5rem' }}>
                      This email is set by your invitation link
                    </p>
                  )}
                </div>
                
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, fontSize: '0.9rem' }}>Password</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem', border: '1px solid #d1d5db', borderRadius: '8px', background: 'white' }}>
                    <Lock size={20} style={{ color: '#9ca3af', flexShrink: 0 }} />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={emailPasswordData.password}
                      onChange={(e) => setEmailPasswordData(prev => ({ ...prev, password: e.target.value }))}
                      placeholder="Create a secure password"
                      required
                      style={{ flex: 1, border: 'none', background: 'transparent', outline: 'none', minWidth: 0, fontSize: '1rem' }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '0.25rem',
                        display: 'flex',
                        alignItems: 'center',
                        color: '#9ca3af',
                        flexShrink: 0
                      }}
                    >
                      {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                  <div style={{ marginTop: '0.5rem', fontSize: '0.7rem', color: '#9ca3af', lineHeight: '1.4' }}>
                    <p style={{ margin: '0 0 0.2rem 0', fontWeight: 400 }}>Password must contain:</p>
                    <ul style={{ margin: '0', paddingLeft: '16px', listStyle: 'disc' }}>
                      <li style={{ marginBottom: '0.1rem' }}>At least 8 characters</li>
                      <li style={{ marginBottom: '0.1rem' }}>One capital letter</li>
                      <li style={{ marginBottom: '0.1rem' }}>One number</li>
                      <li style={{ marginBottom: '0.1rem' }}>One special character (!@#$%^&* etc.)</li>
                    </ul>
                  </div>
                </div>
                
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, fontSize: '0.9rem' }}>Confirm Password</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem', border: '1px solid #d1d5db', borderRadius: '8px', background: 'white' }}>
                    <Lock size={20} style={{ color: '#9ca3af', flexShrink: 0 }} />
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={emailPasswordData.confirmPassword}
                      onChange={(e) => setEmailPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                      placeholder="Confirm your password"
                      required
                      style={{ flex: 1, border: 'none', background: 'transparent', outline: 'none', minWidth: 0, fontSize: '1rem' }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '0.25rem',
                        display: 'flex',
                        alignItems: 'center',
                        color: '#9ca3af',
                        flexShrink: 0
                      }}
                    >
                      {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                </div>
                
                <button
                  type="submit"
                  disabled={isSigningIn}
                  style={{
                    width: '100%',
                    padding: '0.875rem 1.5rem',
                    fontSize: '1rem',
                    fontWeight: 600,
                    borderRadius: '12px',
                    border: 'none',
                    background: isSigningIn ? '#d1d5db' : 'linear-gradient(135deg, #f8049c 0%, #f97316 100%)',
                    color: 'white',
                    cursor: isSigningIn ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem'
                  }}
                >
                  {isSigningIn ? (
                    <>
                      <div className="spinner-small"></div>
                      <span>Creating account...</span>
                    </>
                  ) : (
                    'Create Account'
                  )}
                </button>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BrandOnboarding;

